import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { fetchProfile, fetchSummary, type KpiProfile, type KpiSummary, type SummaryQuery } from '../../lib/kpi';

export type Loadable<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string; httpStatus: number | null; code: string | null }
  | { status: 'ready'; data: T };

function toError(error: unknown): Loadable<never> {
  if (error instanceof ApiError) {
    return { status: 'error', message: error.message, httpStatus: error.status, code: error.code };
  }

  return { status: 'error', message: 'The dashboard could not be reached.', httpStatus: null, code: null };
}

export function useProfile(): Loadable<KpiProfile> {
  const [state, setState] = useState<Loadable<KpiProfile>>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchProfile(controller.signal).then(
      (data) => setState({ status: 'ready', data }),
      (error: unknown) => {
        if (!controller.signal.aborted) setState(toError(error));
      },
    );
    return () => controller.abort();
  }, []);

  return state;
}

/** The figures for a window; null query = nothing to ask (the window is
 *  invalid), which keeps whatever was last shown. A newer window aborts the
 *  request of the older one, so a slow answer can never overwrite a fresh one. */
export function useSummary(query: SummaryQuery | null): Loadable<KpiSummary> {
  const [state, setState] = useState<Loadable<KpiSummary>>({ status: 'loading' });
  const from = query?.from;
  const to = query?.to;
  const operatorIds = query?.operatorIds.join(',') ?? '';

  useEffect(() => {
    if (!from || !to) return;

    const controller = new AbortController();
    setState({ status: 'loading' });
    fetchSummary({ from, to, operatorIds: operatorIds ? operatorIds.split(',').map(Number) : [] }, controller.signal)
      .then(
        (data) => setState({ status: 'ready', data }),
        (error: unknown) => {
          if (!controller.signal.aborted) setState(toError(error));
        },
      );
    return () => controller.abort();
  }, [from, to, operatorIds]);

  return state;
}
